import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Camera, Calendar, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { ASSETS } from "../data/landing";

export default function BookingAck() {
  const { bookingId } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(r => setB(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Booking not found"))
      .finally(() => setLoading(false));
  }, [bookingId]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#FCFAF7]"><Loader2 className="h-6 w-6 animate-spin text-[#7B1113]" /></div>;
  if (err || !b) return <div className="flex min-h-screen items-center justify-center bg-[#FCFAF7] p-8 text-center"><p className="text-[#5E4646]" data-testid="ack-error">{err}</p></div>;

  const paid = b.status === "paid";

  return (
    <main className="min-h-screen bg-[#FCFAF7] px-6 py-14 md:py-20" data-testid="booking-ack">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-3">
          <img src={ASSETS.logo} alt="Hari Om Bol" className="h-10 w-10 rounded-full object-cover" />
          <span className="font-semibold text-[#2A1112]">Hari Om Bol</span>
        </div>

        <div className="overflow-hidden rounded-3xl bg-white shadow-[0_30px_60px_-20px_rgba(123,17,19,0.18)]">
          <div className={`px-7 py-10 text-center ${paid ? "bg-[#7B1113] text-[#FCFAF7]" : "bg-[#F6F2EA] text-[#2A1112]"}`}>
            {paid && <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-[#F5A623]" strokeWidth={1.6} />}
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl" data-testid="ack-title">
              {paid ? "Thank You!" : "Booking " + (b.status || "pending").toUpperCase()}
            </h1>
            <p className="mt-2 text-sm opacity-80 md:text-base">
              {paid ? "Your booking is confirmed." : "This booking is not yet confirmed."}
            </p>
          </div>

          <div className="px-7 py-8">
            <div className="rounded-2xl border border-dashed border-[#7B1113]/25 bg-[#FCFAF7] p-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#7B1113]">Booking ID</p>
              <p className="mt-1 text-2xl font-bold tracking-wider text-[#2A1112]" data-testid="ack-booking-id">{b.booking_id}</p>
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              <Row k="Event" v={b.event_title} />
              {b.event_at && <Row k={<span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" />Date</span>} v={b.event_at} />}
              {b.venue && <Row k={<span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />Venue</span>} v={b.venue} />}
              <Row k="Name" v={b.full_name} />
              <Row k="Phone" v={b.phone} />
              {b.email && <Row k="Email" v={b.email} />}
              <Row k="Pax" v={b.pax} />
              <Row k="Amount Paid" v={`₹${Number(b.total_inr).toFixed(2)}`} highlight />
              {b.razorpay_payment_id && <Row k="Payment ID" v={b.razorpay_payment_id} />}
            </dl>

            {paid && (
              <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[#F5A623]/10 p-4 text-sm text-[#2A1112]" data-testid="ack-note">
                <Camera className="mt-0.5 h-5 w-5 shrink-0 text-[#7B1113]" />
                <p>Please take a screenshot of this page and show it at the event for entry.</p>
              </div>
            )}

            <Link to="/event-booking" className="mt-6 inline-flex items-center justify-center rounded-full border border-[#7B1113]/15 bg-white px-5 py-2.5 text-sm font-semibold text-[#7B1113]">
              Browse more events
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

const Row = ({ k, v, highlight }) => (
  <div className={`flex items-center justify-between gap-4 border-b border-[#7B1113]/8 pb-2 ${highlight ? "pt-2" : ""}`}>
    <dt className="text-[#5E4646]">{k}</dt>
    <dd className={`text-right font-semibold ${highlight ? "text-lg text-[#7B1113]" : "text-[#2A1112]"}`}>{v}</dd>
  </div>
);
